import type { TFunction } from "i18next";
import { byPublisher } from "@/lib/aggregator";
import { ApiError } from "@/lib/api";

export const TEXTAREA =
  "w-full rounded-lg border border-border bg-bg-primary px-3 py-2 text-sm leading-relaxed " +
  "placeholder:text-fg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

export const ICON_BUTTON =
  "shrink-0 rounded-md p-1.5 text-fg-muted transition-colors hover:bg-bg-elevated hover:text-fg-primary " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50";

/**
 * What the server said, in words for a person. A form it refused comes back
 * as a list of {loc, msg} with the field's path in front and "Value error, "
 * before our own sentence: only the sentences are shown, each once.
 */
export function errorText(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) return fallback;
  const detail = err.detail;
  const said = (err.body as { detail?: unknown } | null)?.detail;
  if (!Array.isArray(said)) return detail;
  const sentences = said
    .map((item) => (typeof item?.msg === "string" ? item.msg.replace(/^Value error, /, "") : ""))
    .filter(Boolean);
  return sentences.length > 0 ? [...new Set(sentences)].join(" ") : detail;
}

/**
 * Why a post is where it is, in the operator's words. `reason` is the code the
 * server keeps; `detail` — whatever more there is (the word a rule found, the
 * model's own reason, Telegram's answer).
 */
export function reasonText(t: TFunction, reason: string | null, detail: string | null): string {
  if (!reason) return "";
  if (reason.startsWith("filter_")) {
    const rule = t(`aggregator.filter.kind.${reason.slice(7)}`);
    const found =
      reason === "filter_missing_media" && detail
        ? detail
            .split(", ")
            .map((kind) => t(`aggregator.media.missing_kind.${kind}`, kind))
            .join(", ")
        : detail;
    return found
      ? t("aggregator.reason.filter_found", { rule, found })
      : t("aggregator.reason.filter", { rule });
  }
  // Counted over many posts there is no one percentage, no one number of views.
  if (reason === "duplicate") {
    return detail
      ? t("aggregator.reason.duplicate", { percent: detail })
      : t("aggregator.reason.duplicate_any");
  }
  if (reason === "ai_rejected") {
    return t("aggregator.reason.ai_rejected", {
      check: t(`aggregator.ai_check.${detail}`, detail ?? ""),
    });
  }
  if (reason === "few_views") {
    return detail
      ? t("aggregator.reason.few_views", { views: detail })
      : t("aggregator.reason.few_views_any");
  }
  const known = t(byPublisher(`aggregator.reason.${reason}`), "");
  if (!known) return detail ? `${reason}: ${detail}` : reason;
  // The model's own words and Telegram's own answer are shown as they came;
  // these reasons say everything by themselves.
  const complete = [
    "moderation",
    "stale",
    "removed_at_source",
    "empty",
    "unknown_outcome",
    "internal_error",
    "fill_stopped",
  ];
  return detail && !complete.includes(reason) ? `${known} — ${detail}` : known;
}
